import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const Admins = () => {
  const { admin } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editAdmin, setEditAdmin] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editIsMaster, setEditIsMaster] = useState(false);
  const [editActive, setEditActive] = useState(true);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isMaster = admin?.is_master === true;

  useEffect(() => {
    if (isMaster) fetchAdmins();
    else setLoading(false);
  }, [isMaster]);

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/admins`);
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err.response?.status === 403) setList([]);
      else console.error('Fetch admins error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    const u = (username || '').trim();
    const p = password || '';
    if (!u) { setError('Username is required'); return; }
    if (p.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/register`, { username: u, password: p });
      setMessage(`Admin "${u}" created.`);
      setUsername(''); setPassword(''); setShowForm(false);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (a) => {
    setEditAdmin(a); setEditUsername(a.username); setEditPassword('');
    setEditIsMaster(Boolean(a.is_master)); setEditActive(a.active !== false); setEditError('');
  };
  const closeEdit = () => { setEditAdmin(null); setEditUsername(''); setEditPassword(''); setEditIsMaster(false); setEditActive(true); setEditError(''); };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    if (!editAdmin) return;
    setEditError('');
    const u = (editUsername || '').trim();
    if (!u) { setEditError('Username is required'); return; }
    if (editPassword.length > 0 && editPassword.length < 8) { setEditError('Password must be at least 8 characters (or leave blank to keep current)'); return; }
    setEditSubmitting(true);
    try {
      const body = { username: u };
      if (editPassword.length >= 8) body.password = editPassword;
      if (editAdmin.id !== admin?.id) { body.is_master = editIsMaster; body.active = editActive; }
      await axios.patch(`${API_URL}/auth/admins/${editAdmin.id}`, body);
      setMessage('Admin updated successfully.');
      closeEdit(); fetchAdmins();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update admin');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/auth/admins/${deleteConfirm.id}`);
      setMessage(`Admin "${deleteConfirm.username}" deleted.`);
      setDeleteConfirm(null); fetchAdmins();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete admin');
    } finally {
      setDeleting(false);
    }
  };

  if (!isMaster) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-base font-bold text-slate-800 mb-1">Access Restricted</h2>
        <p className="text-sm text-slate-500">Only the master admin can view and manage admins.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const inputCls = 'w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 bg-slate-50 focus:bg-white transition-all';

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Admins</h1>
          <p className="text-sm text-slate-500 mt-0.5">{list.length} admin{list.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          type="button"
          onClick={() => { setShowForm(!showForm); setError(''); setMessage(''); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all shadow-sm hover:shadow-md active:scale-95"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
        >
          {showForm ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              Cancel
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              Create admin
            </>
          )}
        </button>
      </div>

      {/* Success message */}
      {message && (
        <div className="flex items-start gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl">
          <svg className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-emerald-700">{message}</p>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">New admin</h2>
          <form onSubmit={handleCreateAdmin} className="space-y-3 max-w-sm">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                className={inputCls} placeholder="New admin username" autoComplete="off" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password <span className="text-slate-400 font-normal">(min 8 characters)</span></label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} placeholder="••••••••" autoComplete="new-password" />
            </div>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button type="submit" disabled={submitting}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
              {submitting ? 'Creating…' : 'Create admin'}
            </button>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {list.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-slate-400">No admins yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Username</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Role</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Status</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {list.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{a.username}</td>
                    <td className="px-5 py-3.5">
                      {a.is_master ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Master
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Admin
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {a.active !== false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button type="button" onClick={() => openEdit(a)}
                          className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors" title="Edit">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        </button>
                        {a.id !== admin?.id && (
                          <button type="button" onClick={() => setDeleteConfirm(a)}
                            className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors" title="Delete">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Delete admin?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  <strong>{deleteConfirm.username}</strong> will no longer be able to log in.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteAdmin} disabled={deleting}
                className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-xl hover:bg-rose-700 disabled:opacity-50 text-sm font-semibold">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit admin modal */}
      {editAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeEdit} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-bold text-slate-900 mb-4">Edit admin</h3>
            <form onSubmit={handleUpdateAdmin} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Username</label>
                <input type="text" value={editUsername} onChange={(e) => setEditUsername(e.target.value)}
                  className={inputCls} required autoComplete="off" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New password <span className="text-slate-400 font-normal">(leave blank to keep current)</span></label>
                <input type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)}
                  className={inputCls} placeholder="Leave blank to keep current" autoComplete="new-password" />
              </div>
              {editAdmin.id !== admin?.id && (
                <div className="space-y-2 pt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={editIsMaster} onChange={(e) => setEditIsMaster(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                    <span className="text-sm text-slate-700">Master (can create/edit admins)</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                    <span className="text-sm text-slate-700">Active (can log in)</span>
                  </label>
                </div>
              )}
              {editError && <p className="text-sm text-rose-600">{editError}</p>}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={closeEdit}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={editSubmitting}
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  {editSubmitting ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
